import React, { useState, useEffect } from "react";

const BASE = "/api";
const auth = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || localStorage.getItem("access_token")}`,
});
const apiFetch = async (path, opts = {}) => {
  const r = await fetch(BASE + path, { headers: auth(), ...opts });
  const d = await r.json();
  if (!r.ok) throw new Error(d.error || "Request failed");
  return d;
};

const fmtPct = (v, sign = false) => {
  if (v == null) return "—";
  const n = Number(v);
  return (sign && n > 0 ? "+" : "") + n.toFixed(2) + "%";
};

const card = {
  background: "var(--bg-surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-xl)",
  padding: 24,
  marginBottom: 20,
};
const btn = (v = "primary") => ({
  padding: "10px 20px", borderRadius: "var(--radius-lg)", border: "none",
  cursor: "pointer", fontWeight: 600, fontSize: 13,
  background: v === "primary" ? "var(--accent)" : "var(--bg-elevated)",
  color: v === "primary" ? "#fff" : "var(--text-main)",
});
const inp = {
  width: "100%", padding: "9px 12px", borderRadius: "var(--radius-lg)",
  border: "1px solid var(--border)", background: "var(--bg-elevated)",
  color: "var(--text-main)", fontSize: 13, boxSizing: "border-box",
};

const SyncMark = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="8" stroke="var(--accent)" strokeWidth="1.5" strokeDasharray="3 2" />
    <circle cx="9" cy="9" r="3.5" fill="var(--accent)" opacity="0.9" />
    <line x1="9" y1="1"    x2="9"  y2="4.5" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="9" y1="13.5" x2="9"  y2="17"  stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="1" y1="9"    x2="4.5" y2="9"  stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="13.5" y1="9" x2="17"  y2="9"  stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const CompareBar = ({ liveVal, planVal, isDD = false }) => {
  const l = Number(liveVal) || 0;
  const p = Number(planVal) || 0;
  const max = Math.max(l, p, 0.01);
  const liveColor = isDD ? "var(--danger)" : "var(--accent)";
  const planColor = isDD
    ? p < l ? "var(--success)" : "var(--danger)"
    : p > l ? "var(--success)" : "var(--warning)";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      {[["LIVE", l, liveColor], ["PLAN", p, planColor]].map(([label, val, color]) => (
        <div key={label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 10, color: "var(--text-faint)", width: 32, fontWeight: 600 }}>{label}</span>
          <div style={{ flex: 1, height: 6, background: "var(--bg-base)", borderRadius: 3, overflow: "hidden" }}>
            <div style={{ width: `${(val / max) * 100}%`, height: "100%", background: color, borderRadius: 3, transition: "width 0.7s ease" }} />
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, color, width: 56, textAlign: "right" }}>
            {isDD ? fmtPct(val) : fmtPct(val, true)}
          </span>
        </div>
      ))}
    </div>
  );
};

const Delta = ({ live, plan, isDD = false }) => {
  const diff = (Number(plan) || 0) - (Number(live) || 0);
  const good = isDD ? diff < 0 : diff > 0;
  if (Math.abs(diff) < 0.1) return null;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
      background: good ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.10)",
      color: good ? "var(--success)" : "var(--danger)",
    }}>
      {good ? "▲" : "▼"} {Math.abs(diff).toFixed(1)}%
    </span>
  );
};

export default function BecomeAnalystCard({ accountId }) {
  const [status, setStatus]         = useState(null);
  const [preview, setPreview]       = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [modal, setModal]           = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg]               = useState("");
  const [consent, setConsent]       = useState(false);
  const [activateResult, setActivateResult] = useState(null);

  const load = () => {
    if (!accountId) return;
    apiFetch(`/trader/signalsync-status?account_id=${accountId}`).then(setStatus).catch(() => {});
  };

  useEffect(() => { setStatus(null); setPreview(null); load(); }, [accountId]);

  // Auto-fetch preview saat accountId tersedia
  useEffect(() => {
    if (!accountId) return;
    setPreviewLoading(true);
    apiFetch(`/trader/signalsync-preview?account_id=${accountId}`)
      .then(d => setPreview(d))
      .catch(() => setPreview(null))
      .finally(() => setPreviewLoading(false));
  }, [accountId]);

  const handleApply = async () => {
    if (!accountId || !consent) return;
    setSubmitting(true);
    try {
      const res = await apiFetch("/trader/signalsync-activate", {
        method: "POST",
        body: JSON.stringify({ account_id: accountId }),
      });
      setMsg("");
      setActivateResult(res);
      load();
    } catch (e) { setMsg(e.message); }
    setSubmitting(false);
  };

  if (!status) return null;

  const live = preview?.live || {};
  const plan = preview?.plan || {};
  const insight = preview?.insight || "";
  const planBetter = (Number(plan.roi) > Number(live.roi)) || (Number(plan.max_dd) < Number(live.max_dd));
  const hasData = preview && (plan.total_trades > 0);

  // ── ACTIVE ───────────────────────────────────────────────────────────────────
  if (status.activated && status.activation?.status === "ACTIVE") {
    const s = status.activation;
    return (
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--success)", boxShadow: "0 0 6px var(--success)" }} />
            <SyncMark size={15} />
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--success)", letterSpacing: "0.06em" }}>SIGNALSYNC™ ACTIVE</span>
          </div>
          <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-faint)", background: "var(--bg-elevated)", padding: "3px 10px", borderRadius: 20 }}>
            Auto-Sync   
          </span>
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-main)", marginBottom: 2 }}>{s.strategy_name || "My Signal Plan"}</div>
        {s.market_focus && (
          <div style={{ fontSize: 12, color: "var(--text-faint)", marginBottom: 18 }}>{s.market_focus}</div>
        )}

        {/* Live vs Plan comparison kalau data tersedia */}
        {hasData && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-faint)", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Live vs Plan Analysis
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 12 }}>
              {[
                { label: "ROI", liveVal: live.roi, planVal: plan.roi, isDD: false },
                { label: "Win Rate", liveVal: live.win_rate, planVal: plan.win_rate, isDD: false },
                { label: "Max DD", liveVal: live.max_dd, planVal: plan.max_dd, isDD: true },
              ].map(({ label, liveVal, planVal, isDD }) => (
                <div key={label} style={{ background: "var(--bg-elevated)", borderRadius: "var(--radius-lg)", padding: "12px 14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "var(--text-faint)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
                    <Delta live={liveVal} plan={planVal} isDD={isDD} />
                  </div>
                  <CompareBar liveVal={liveVal} planVal={planVal} isDD={isDD} />
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
              {[
                ["Analyzed", plan.total_trades],
                ["Wins",     plan.wins],
                ["Losses",   plan.losses],
                ["Net P&L",  `$${Number(plan.net_profit||0).toFixed(2)}`],
              ].map(([label, value]) => (
                <div key={label} style={{ background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", padding: "10px 8px", textAlign: "center" }}>
                  <div style={{ fontSize: 10, color: "var(--text-faint)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-main)" }}>{value ?? "—"}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: "var(--success)", fontWeight: 600 }}>
            Your plan is live on the marketplace.
          </span>
        </div>
      </div>
    );
  }

  // ── DECLINED ─────────────────────────────────────────────────────────────────
  if (status.activated && status.activation?.status === "DECLINED") {
    return (
      <div style={{ ...card, borderColor: "rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.03)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <SyncMark size={15} />
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--danger)", letterSpacing: "0.07em" }}>SIGNALSYNC™</span>
          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 600,
            background: "rgba(239,68,68,0.12)", color: "var(--danger)" }}>
            DECLINED — Margin Call
          </span>
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.8, marginBottom: 14 }}>
          Your plan simulation reached <strong style={{color:"var(--danger)"}}>100% drawdown (margin call)</strong>.
          Following your SL/TP plan strictly would have blown the account — this does not meet marketplace safety standards.
        </div>
        <div style={{
          fontSize: 12, color: "var(--text-muted)", lineHeight: 1.7,
          background: "var(--bg-elevated)", borderLeft: "3px solid var(--danger)",
          borderRadius: "var(--radius-md)", padding: "10px 14px",
        }}>
          To qualify: trade consistently with SL &amp; TP, avoid oversized positions, and keep plan max DD below 100%.
          Once your plan performance improves, you can re-apply.
        </div>
      </div>
    );
  }

  // ── SUSPENDED ────────────────────────────────────────────────────────────────
  if (status.activated && status.activation?.status === "SUSPENDED") {
    const act = status.activation;
    const holdDays = act.hold_days_left || 0;
    return (
      <div style={{ ...card, borderColor: "rgba(245,158,11,0.3)", background: "rgba(245,158,11,0.02)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <SyncMark size={15} />
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--warning)", letterSpacing: "0.07em" }}>SIGNALSYNC™</span>
          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 600,
            background: "rgba(245,158,11,0.12)", color: "var(--warning)" }}>
            SUSPENDED
          </span>
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.8, marginBottom: 14 }}>
          Your plan's risk-adjusted performance has dropped below the marketplace threshold.
          Your signals have been temporarily removed — <strong style={{color:"var(--text-main)"}}>no action needed</strong>.
          They will resume automatically when your plan performance recovers.
        </div>
        {holdDays > 0 && (
          <div style={{
            fontSize: 12, color: "var(--text-muted)", lineHeight: 1.7,
            background: "var(--bg-elevated)", borderLeft: "3px solid var(--warning)",
            borderRadius: "var(--radius-md)", padding: "10px 14px", marginBottom: 14,
          }}>
             Hold period active — protected from suspension for <strong style={{color:"var(--warning)"}}>{holdDays} more day{holdDays!==1?"s":""}</strong>.
            System will re-evaluate after hold expires.
          </div>
        )}
        {!previewLoading && hasData && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
            {[
              { label: "ROI", liveVal: live.roi, planVal: plan.roi, isDD: false },
              { label: "Win Rate", liveVal: live.win_rate, planVal: plan.win_rate, isDD: false },
              { label: "Max Drawdown", liveVal: live.max_dd, planVal: plan.max_dd, isDD: true },
            ].map(({ label, liveVal, planVal, isDD }) => (
              <div key={label} style={{ background: "var(--bg-elevated)", borderRadius: "var(--radius-lg)", padding: "12px 14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: "var(--text-faint)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
                  <Delta live={liveVal} plan={planVal} isDD={isDD} />
                </div>
                <CompareBar liveVal={liveVal} planVal={planVal} isDD={isDD} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── AWAITING / PENDING ───────────────────────────────────────────────────────
  if (status.activated && ["AWAITING","PENDING"].includes(status.activation?.status)) {
    return (
      <div style={{ ...card, borderColor: "rgba(78,154,241,0.25)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <SyncMark size={15} />
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--accent)", letterSpacing: "0.07em" }}>SIGNALSYNC™</span>
          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 600,
            background: "rgba(78,154,241,0.1)", color: "var(--accent)" }}>
            AWAITING ELIGIBILITY
          </span>
        </div>

        <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.7, marginBottom: 18 }}>
          Your account is registered. Your plan will publish automatically once it meets the marketplace
          risk-adjusted criteria — <strong style={{color:"var(--text-main)"}}>no action needed</strong>.
          Keep trading with SL &amp; TP on every position.
        </div>

        {previewLoading && (
          <div style={{ fontSize: 12, color: "var(--text-faint)", padding: "12px 0" }}>
            Analyzing your trade history...
          </div>
        )}

        {!previewLoading && hasData && (
          <>
            {insight && (
              <div style={{
                fontSize: 12, color: "var(--text-muted)", lineHeight: 1.7,
                background: "var(--bg-elevated)",
                borderLeft: `3px solid ${planBetter ? "var(--success)" : "var(--warning)"}`,
                borderRadius: "var(--radius-md)", padding: "10px 14px", marginBottom: 14,
              }}>{insight}</div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 12 }}>
              {[
                { label: "ROI", liveVal: live.roi, planVal: plan.roi, isDD: false },
                { label: "Win Rate", liveVal: live.win_rate, planVal: plan.win_rate, isDD: false },
                { label: "Max Drawdown", liveVal: live.max_dd, planVal: plan.max_dd, isDD: true },
              ].map(({ label, liveVal, planVal, isDD }) => (
                <div key={label} style={{ background: "var(--bg-elevated)", borderRadius: "var(--radius-lg)", padding: "12px 14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "var(--text-faint)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
                    <Delta live={liveVal} plan={planVal} isDD={isDD} />
                  </div>
                  <CompareBar liveVal={liveVal} planVal={planVal} isDD={isDD} />
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
              {[
                ["Analyzed", plan.total_trades],
                ["Wins",     plan.wins],
                ["Losses",   plan.losses],
                ["Net P&L",  `$${Number(plan.net_profit||0).toFixed(2)}`],
              ].map(([label, value]) => (
                <div key={label} style={{ background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", padding: "10px 8px", textAlign: "center" }}>
                  <div style={{ fontSize: 10, color: "var(--text-faint)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-main)" }}>{value ?? "—"}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  // ── CTA + PREVIEW ────────────────────────────────────────────────────────────
  return (
    <>
      <div style={{
        ...card,
        background: "linear-gradient(135deg, var(--bg-surface) 0%, rgba(78,154,241,0.05) 100%)",
        border: "1px solid rgba(78,154,241,0.18)",
      }}>
        {/* Brand row */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <SyncMark size={16} />
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--accent)", letterSpacing: "0.08em" }}>SIGNALSYNC™</span>
          {planBetter && hasData && (
            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 600, background: "rgba(78,154,241,0.1)", color: "var(--accent)" }}>
              Plan outperforms Live
            </span>
          )}
        </div>

        {/* Headline + subline */}
        <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-main)", marginBottom: 6, lineHeight: 1.4 }}>
          Your discipline is your product.
        </div>
        <div style={{ fontSize: 13, color: "var(--text-faint)", marginBottom: 6, lineHeight: 1.7 }}>
          SignalSync™ converts your real trading activity into structured investment plans.
          Every position with SL &amp; TP becomes a signal plan — subscribers back your thesis, not your execution.
        </div>
        <div style={{ fontSize: 12, color: "var(--accent)", fontStyle: "italic", marginBottom: 18, paddingLeft: 10, borderLeft: "2px solid var(--accent)" }}>
          We don't publish trades. We publish plans.
        </div>

        {/* Preview section — muncul kalau ada data */}
        {previewLoading && (
          <div style={{ fontSize: 12, color: "var(--text-faint)", padding: "12px 0", marginBottom: 16 }}>
            Analyzing your trade history...
          </div>
        )}

        {!previewLoading && hasData && (
          <div style={{ marginBottom: 20 }}>
            {/* Insight */}
            {insight && (
              <div style={{
                fontSize: 12, color: "var(--text-muted)", lineHeight: 1.7,
                background: "var(--bg-elevated)",
                borderLeft: `3px solid ${planBetter ? "var(--success)" : "var(--warning)"}`,
                borderRadius: "var(--radius-md)", padding: "10px 14px", marginBottom: 14,
              }}>{insight}</div>
            )}

            {/* Compare grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 12 }}>
              {[
                { label: "ROI", liveVal: live.roi, planVal: plan.roi, isDD: false },
                { label: "Win Rate", liveVal: live.win_rate, planVal: plan.win_rate, isDD: false },
                { label: "Max Drawdown", liveVal: live.max_dd, planVal: plan.max_dd, isDD: true },
              ].map(({ label, liveVal, planVal, isDD }) => (
                <div key={label} style={{ background: "var(--bg-elevated)", borderRadius: "var(--radius-lg)", padding: "12px 14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "var(--text-faint)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
                    <Delta live={liveVal} plan={planVal} isDD={isDD} />
                  </div>
                  <CompareBar liveVal={liveVal} planVal={planVal} isDD={isDD} />
                </div>
              ))}
            </div>

            {/* Summary mini cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 4 }}>
              {[
                ["Analyzed", plan.total_trades],
                ["Wins",     plan.wins],
                ["Losses",   plan.losses],
                ["Net P&L",  `$${Number(plan.net_profit||0).toFixed(2)}`],
              ].map(([label, value]) => (
                <div key={label} style={{ background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", padding: "10px 8px", textAlign: "center" }}>
                  <div style={{ fontSize: 10, color: "var(--text-faint)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-main)" }}>{value ?? "—"}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!previewLoading && !hasData && (
          <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
            {["Plan-driven, not copy-based","Verified track record","Earn from subscribers"].map(f => (
              <span key={f} style={{ fontSize: 11, color: "var(--text-muted)", background: "var(--bg-elevated)", padding: "4px 10px", borderRadius: 20, display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ color: "var(--success)", fontSize: 10 }}></span> {f}
              </span>
            ))}
          </div>
        )}

        <button style={btn()} onClick={() => setModal(true)}>
          Activate SignalSync™ →
        </button>
      </div>

      {/* Modal */}
      {modal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "var(--bg-surface)", borderRadius: "var(--radius-xl)", padding: 28, width: "100%", maxWidth: 480, border: "1px solid var(--border)" }}>

            {!activateResult ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <SyncMark size={16} />
                  <span style={{ fontSize: 16, fontWeight: 700, color: "var(--text-main)" }}>Activate SignalSync™</span>
                </div>
                <div style={{ fontSize: 12, color: "var(--text-faint)", marginBottom: 20, lineHeight: 1.7 }}>
                  Once activated, every position you open with SL &amp; TP set will be evaluated as a structured
                  signal plan under your trader profile — no separate identity or strategy name needed.
                  If your plan performance meets the marketplace criteria, it publishes automatically;
                  if not, it stays private until your track record qualifies.
                </div>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 20, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={e => setConsent(e.target.checked)}
                    style={{ marginTop: 2, width: 16, height: 16, cursor: "pointer", accentColor: "var(--accent)" }}
                  />
                  <span style={{ fontSize: 12, color: "var(--text-main)", lineHeight: 1.6 }}>
                    I understand and agree to publish my plan performance for SignalSync™.
                  </span>
                </label>
                {msg && <div style={{ fontSize: 12, color: "var(--danger)", marginBottom: 12 }}>{msg}</div>}
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button style={btn("secondary")} onClick={() => { setModal(false); setMsg(""); }}>Cancel</button>
                  <button style={btn()} onClick={handleApply} disabled={submitting || !consent}>{submitting ? "Activating..." : "Activate →"}</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%",
                    background: activateResult.auto_approved ? "rgba(34,197,94,0.12)" : "rgba(78,154,241,0.1)",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
                  }}>{activateResult.auto_approved ? "" : "⏳"}</div>
                  <span style={{ fontSize: 15, fontWeight: 700, color: "var(--text-main)" }}>
                    {activateResult.auto_approved ? "SignalSync™ is now Active!" : "Application Submitted"}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.7, marginBottom: 20 }}>
                  {activateResult.auto_approved
                    ? "Your plan already meets the marketplace criteria — it's published and visible to investors now."
                    : "Your track record doesn't yet meet the marketplace threshold. Keep trading with SL & TP — once your plan qualifies, it will publish automatically without needing to re-apply."}
                </div>
                <button style={{ ...btn(), width: "100%" }} onClick={() => { setModal(false); setActivateResult(null); }}>
                  Got it →
                </button>
              </>
            )}

          </div>
        </div>
      )}
    </>
  );
}
